/**
 * MONEY. What is owed and what is late, without opening a spreadsheet. Priority 5 of the brief.
 *
 * Square is where the money moves; this is the book beside it that answers the one question Square
 * cannot: which household still owes what, against which visit, and since when. Late invoices sit
 * at the top in coral, then everything else by due date, so the first thing on the screen is the
 * first phone call to make.
 *
 * ON A DESK IT IS A TABLE, and the argument the phone makes with its order the table makes with
 * three captions: past the due date, owed and not yet late, nothing owed. Three columns sort, and
 * A SORT NEVER CROSSES A GROUP: the late ones keep the top of the list whichever way a column is
 * pointed, because which invoices are late is the screen's whole point and a sort is only a way of
 * looking inside that. An invoice with no due date sits at the end of its group either way, since
 * an unsent draft is neither the earliest nor the latest thing here.
 *
 * CORAL APPEARS ONLY ON LATE. The state pill says what the invoice is, and a second coral pill
 * beside it says the gap nobody has closed. Draft, sent, part paid and void are facts, and a fact
 * is not a gap. How late it is comes from the server's own day, never from the phone's clock.
 *
 * Every read here asks for a recent sign-in. `docs/identity.md` decided against a global idle timer
 * and for re-authentication on exactly two screens, and this is one of them: an owner's phone left
 * showing every client's balance is the failure it names. The server answers with 403 and a sign-in
 * link that already knows to come back here, and this screen renders that link and nothing else.
 *
 * There is no figure typed anywhere on this screen. Prices come from the card through the visit,
 * and the one place a dollar amount is typed in the money screens is a payment that arrived.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, listInvoices, needsStepUp, type InvoiceListRow } from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import {
  DataTable,
  FilterBar,
  FilterToggle,
  SortableHeader,
  type DataColumn,
  type RowGroup,
  type SortDirection,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, StepUpNotice } from '../components/Notice.tsx';
import { GapPill } from '../components/StatusPills.tsx';
import { daysBetween, formatDay, money, orderInvoices, shortDay } from '../format.ts';
import { INVOICE_STATE_LABELS } from '../labels.ts';

/** The three columns worth sorting. The rest of a row is read once you have found it. */
type SortKey = 'due' | 'household' | 'balance';

interface Ordering {
  readonly key: SortKey;
  readonly direction: Exclude<SortDirection, 'none'>;
}

/** What the screen opens on, and what `orderInvoices` already puts the rows in. */
const FIRST: Ordering = { key: 'due', direction: 'ascending' };

function compare(key: SortKey, a: InvoiceListRow, b: InvoiceListRow): number {
  if (key === 'household') return a.clientName.localeCompare(b.clientName);
  if (key === 'balance') return a.balanceCents - b.balanceCents;
  if (a.dueOn === null || b.dueOn === null || a.dueOn === b.dueOn) return 0;
  return a.dueOn < b.dueOn ? -1 : 1;
}

/** Stable: rows the chosen column cannot separate keep the order the server gave them. */
function sorted(rows: readonly InvoiceListRow[], by: Ordering): readonly InvoiceListRow[] {
  const flip = by.direction === 'descending' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      if (by.key === 'due' && (a.row.dueOn === null) !== (b.row.dueOn === null)) {
        return a.row.dueOn === null ? 1 : -1;
      }
      const step = compare(by.key, a.row, b.row) * flip;
      return step === 0 ? a.index - b.index : step;
    })
    .map((entry) => entry.row);
}

/** "3 days late", measured against the day the server reported with the list. */
function lateWords(dueOn: string | null, today: string): string {
  const days = dueOn === null ? null : daysBetween(dueOn, today);
  if (days === null || days < 1) return 'Late';
  return days === 1 ? '1 day late' : `${days} days late`;
}

export function Money() {
  const [includeSettled, setIncludeSettled] = useState(false);
  const [answer, setAnswer] = useState<{ today: string; invoices: InvoiceListRow[] } | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [by, setBy] = useState<Ordering>(FIRST);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    setAnswer(null);
    listInvoices(!includeSettled, null, '/money')
      .then((given) => {
        if (live) setAnswer(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The invoices did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [includeSettled, attempt]);

  const turn = (key: SortKey) =>
    setBy((current) =>
      current.key === key
        ? { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' }
        : { key, direction: 'ascending' },
    );
  const pointing = (key: SortKey): SortDirection => (by.key === key ? by.direction : 'none');

  const rows = answer ? orderInvoices(answer.invoices) : null;
  const groups: readonly RowGroup<InvoiceListRow>[] = rows
    ? [
        {
          key: 'late',
          caption: 'Past the due date',
          rows: sorted(
            rows.filter((row) => row.late),
            by,
          ),
        },
        {
          key: 'owed',
          caption: 'Owed, and not yet late',
          rows: sorted(
            rows.filter((row) => !row.late && row.balanceCents > 0),
            by,
          ),
        },
        {
          key: 'settled',
          caption: 'Nothing owed',
          rows: sorted(
            rows.filter((row) => !row.late && row.balanceCents <= 0),
            by,
          ),
        },
      ].filter((group) => group.rows.length > 0)
    : [];

  return (
    <div>
      <ScreenTitle title="Money" subtitle="What is owed and what is late." />

      <FilterBar>
        <FilterToggle
          id="money-include-settled"
          label="Include paid and void invoices"
          checked={includeSettled}
          onChange={setIncludeSettled}
        />
      </FilterBar>

      {error ? (
        needsStepUp(error) ? (
          <StepUpNotice error={error} />
        ) : (
          <ErrorNotice title="The invoices did not load" message={error.message} onRetry={reload} />
        )
      ) : rows === null || answer === null ? (
        <Busy what="the invoices" />
      ) : rows.length === 0 ? (
        <EmptyNotice title={includeSettled ? 'No invoices yet' : 'Nothing is owed'}>
          <p>
            {includeSettled
              ? 'An invoice is drafted from a finished visit, on the visit itself.'
              : 'Every invoice is paid or void. Tick the box above to see them.'}
          </p>
        </EmptyNotice>
      ) : (
        <>
          <p className={`mb-2 hidden text-xs text-navy/60 md:block ${MEASURE}`}>
            Late first, then by due date. Sort a column to look at it another way; the late ones
            keep the top of the list.
          </p>
          <DataTable
            label="Invoices"
            groups={groups}
            columns={columnsFor(pointing, turn, answer.today)}
            rowKey={(row) => row.id}
            card={(row) => <InvoiceCard row={row} />}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The table, at md and above. The same rows the cards are drawn from.
 * ------------------------------------------------------------------ */

/**
 * THE WIDTHS ARE SHARES, NOT PIXELS, and they are the artboard's proportions rather than its
 * measurements. A fixed table layout takes the greater of its own width and the sum of its
 * columns, so 128 plus 150 plus 150 plus 104 plus 124 is 656px of table in the 470px the rail and
 * the page padding leave at the md breakpoint. The wrapper clipped instead of scrolling, so the
 * household column collapsed to nothing and Owed sat off the right edge with no way to reach it:
 * the screen that exists to say what is owed showed neither figure. A share always resolves against
 * the room the table has, so every column is on the screen wherever the room allows. Since ops #56
 * the wrapper scrolls in its own frame below the floor `tableMinWidth` sets, rather than clipping.
 */
function columnsFor(
  pointing: (key: SortKey) => SortDirection,
  turn: (key: SortKey) => void,
  today: string,
): readonly DataColumn<InvoiceListRow>[] {
  return [
    {
      key: 'household',
      sort: pointing('household'),
      header: (
        <SortableHeader
          label="Household"
          direction={pointing('household')}
          onSort={() => turn('household')}
        />
      ),
      cell: (row) => (
        <Link
          to={`/invoices/${row.id}`}
          className="flex min-h-11 desk:min-h-9 items-center font-serif text-base"
        >
          <span className="break-words">{row.clientName}</span>
        </Link>
      ),
    },
    {
      key: 'visit',
      header: 'Visit',
      width: '11%',
      cellClassName: 'text-navy/75',
      // The invoice by its number first, where it has one (ops #76 O2), then the day of its clean.
      cell: (row) =>
        row.number ? (
          <>
            <span className="block font-medium text-navy">{row.number}</span>
            <span className="block">{shortDay(row.visitDate)}</span>
          </>
        ) : (
          shortDay(row.visitDate)
        ),
    },
    {
      key: 'due',
      sort: pointing('due'),
      width: '13%',
      header: <SortableHeader label="Due" direction={pointing('due')} onSort={() => turn('due')} />,
      cell: (row) =>
        row.dueOn === null ? (
          <span className="text-navy/60">Not sent yet</span>
        ) : (
          <>
            <span className="block">{shortDay(row.dueOn)}</span>
            {row.late ? (
              <span className="block text-xs font-medium text-coral">
                {lateWords(row.dueOn, today)}
              </span>
            ) : null}
          </>
        ),
    },
    {
      key: 'state',
      header: 'State',
      width: '13%',
      cell: (row) => (
        <div className="flex flex-wrap gap-1.5">
          <Pill tone={row.state === 'paid' ? 'sage' : row.state === 'draft' ? 'gold' : 'quiet'}>
            {INVOICE_STATE_LABELS[row.state]}
          </Pill>
          {row.late ? <GapPill>Late</GapPill> : null}
        </div>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      width: '10%',
      cellClassName: 'text-navy/75',
      cell: (row) => money(row.totalCents),
    },
    {
      key: 'balance',
      sort: pointing('balance'),
      width: '11%',
      header: (
        <SortableHeader
          label="Owed"
          direction={pointing('balance')}
          onSort={() => turn('balance')}
        />
      ),
      cell: (row) =>
        row.balanceCents > 0 ? (
          <span className="font-semibold">{money(row.balanceCents)}</span>
        ) : (
          <span className="text-navy/60">Nothing</span>
        ),
    },
  ];
}

function InvoiceCard({ row }: { row: InvoiceListRow }) {
  return (
    <Link to={`/invoices/${row.id}`} className="block">
      <Card className={row.late ? 'border-coral/40' : ''}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-serif text-lg leading-tight">{row.clientName}</h2>
          {row.late ? <Pill tone="coral">Late</Pill> : null}
          <Pill tone={row.state === 'paid' ? 'sage' : row.state === 'draft' ? 'gold' : 'quiet'}>
            {INVOICE_STATE_LABELS[row.state]}
          </Pill>
        </div>
        <p className="mt-1 text-sm text-navy/75">
          {row.number ? `${row.number}, visit on ` : 'Visit on '}
          {shortDay(row.visitDate)}
        </p>
        <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm">
          <span>Total {money(row.totalCents)}</span>
          <span className={row.balanceCents > 0 ? 'font-semibold' : 'text-navy/60'}>
            {row.balanceCents > 0 ? `Owed ${money(row.balanceCents)}` : 'Nothing owed'}
          </span>
        </div>
        <p className="mt-1 text-xs text-navy/60">
          {row.dueOn ? `Due ${formatDay(row.dueOn)}` : 'Not yet sent, so no due date.'}
        </p>
      </Card>
    </Link>
  );
}
